import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import { useI18n, languageEnglishName } from '../../features/i18n';
import dataService from '../../services/dataService';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/chat — rebuild of the diner chat in brand primitives. Two-column
 * layout on wide (messages left, order rail right). Order rail lifts items
 * the LLM suggests into a persistent "Your picks" list — add / remove / bump
 * quantity — then "Send to kitchen" drops a summary message into the chat.
 *
 * Source of logic: recovery ChatScreen (OrderPanel + addToOrder / removeFromOrder
 * / updateOrderQty / placeOrder). Source of design: brand tokens + Nunito +
 * cwm-sheet cards + pill buttons + framer-motion choreography.
 */

const EASE = [0.22, 1, 0.36, 1];

export default function UnifiedChat() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { userId, isInitialized } = useContext(UserContext);
  const { t, lang } = useI18n();

  // Entry points — in priority order:
  //   1. location.state from in-app navigation (RestaurantDetail → chat)
  //   2. URL query params from a QR code scan (`?r=12&t=Table+5`)
  //
  // URL params win when state is missing; state wins otherwise. The
  // `t` param is the table label, surfaced in the hero and used in the
  // AI preamble so suggestions scale for the right party size.
  const restaurantId   = location.state?.restaurantId   || searchParams.get('r')  || null;
  const stateRestName  = location.state?.restaurantName || null;
  const tableLabel     = location.state?.tableLabel     || searchParams.get('t')  || null;
  const [fetchedName, setFetchedName] = useState(null);
  const restaurantName = stateRestName || fetchedName || 'Restaurant';

  // When we arrive from a QR scan we have an id but no name — fetch it
  // so the hero reads like the in-app flow.
  useEffect(() => {
    if (stateRestName || !restaurantId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await dataService.getRestaurant(restaurantId);
        if (!cancelled) setFetchedName(data?.name || null);
      } catch { /* fine — falls back to 'Restaurant' */ }
    })();
    return () => { cancelled = true; };
  }, [restaurantId, stateRestName]);

  // Track whether we've already told the AI to switch languages in this
  // session. A one-line preamble attached to the FIRST message of the
  // chosen language is enough — the model keeps responding in that
  // language for the rest of the conversation. We re-trigger whenever
  // `lang` changes mid-chat.
  const announcedLangRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [menuPicks, setMenuPicks] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [alert, setAlert] = useState(null);
  const [input, setInput] = useState('');

  const scrollRef = useRef(null);

  useEffect(() => {
    if (!isInitialized || !restaurantId || !userId) return;
    (async () => {
      try {
        const data = await dataService.startChat(userId, restaurantId);
        setChatId(data.id);
        setMessages(JSON.parse(data.conversation_data));
      } catch (e) {
        setAlert({ tone: 'red', text: e?.message || 'Unable to start chat.' });
      }
    })();
  }, [isInitialized, userId, restaurantId]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (chatId < 0 || messages.length < 4) return;
    (async () => {
      try {
        const data = await dataService.menuItemsFromChat(chatId);
        const items = Array.isArray(data?.items) ? data.items.map((it, i) => typeof it === 'string'
          ? { id: `chat-${chatId}-${i}`, name: it, price: 0 }
          : it
        ) : [];
        setMenuPicks(items);
      } catch {}
    })();
  }, [messages, chatId]);

  // Track whether we've already told the AI about the table number in this
  // session. Same one-shot pattern as the language preamble.
  const announcedTableRef = useRef(null);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg) return;
    if (!userId) { setAlert({ tone: 'red', text: t('chat.send_failed') }); return; }
    if (!restaurantId) { setAlert({ tone: 'red', text: t('chat.send_failed') }); return; }
    if (chatId < 0) { setAlert({ tone: 'amber', text: t('common.loading') }); return; }
    setInput('');
    // Show the raw user message in the UI (without the preamble) so the
    // transcript stays clean. Only the outgoing payload gets the preamble.
    setMessages((prev) => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      // Build the outgoing preamble. Keep it terse — the model just needs
      // context on language + table, then conversation history pins it.
      const preambles = [];
      if (lang && lang !== 'en' && announcedLangRef.current !== lang) {
        preambles.push(`Please respond in ${languageEnglishName(lang)} from now on.`);
        announcedLangRef.current = lang;
      }
      if (tableLabel && announcedTableRef.current !== tableLabel) {
        preambles.push(`(Diner is seated at table ${tableLabel}.)`);
        announcedTableRef.current = tableLabel;
      }
      const outgoing = preambles.length > 0 ? `${preambles.join('\n')}\n\n${msg}` : msg;
      const data = await dataService.sendMessage(chatId, outgoing, { language: lang, table: tableLabel || undefined });
      setMessages((prev) => [...prev, data.response]);
    } catch (e) {
      setAlert({ tone: 'red', text: e?.message || t('chat.send_failed') });
    } finally {
      setLoading(false);
    }
  };

  const addToOrder = (item) => {
    const id = item.id || item.menu_item_id;
    const price = item.unit_price_cents || item.price_cents || item.price || 0;
    const name = item.name || item.title || 'Item';
    setOrderItems((prev) => {
      const idx = prev.findIndex((i) => i.id === id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        return next;
      }
      return [...prev, { id, name, price, qty: 1 }];
    });
  };
  const removeFromOrder = (id) => setOrderItems((prev) => prev.filter((i) => i.id !== id));
  const updateQty = (id, qty) => {
    if (qty <= 0) return removeFromOrder(id);
    setOrderItems((prev) => prev.map((i) => i.id === id ? { ...i, qty } : i));
  };
  const placeOrder = () => {
    if (!orderItems.length) return;
    const summary = orderItems.map((i) => `${i.qty}× ${i.name}`).join(', ');
    send(`I'd like to place my order: ${summary}`);
    setAlert({ tone: 'green', text: 'Order sent to the kitchen!' });
    setOrderItems([]);
  };
  const newChat = async () => {
    if (!userId || !restaurantId) return;
    try {
      const data = await dataService.startChat(userId, restaurantId, true);
      setChatId(data.id);
      setMessages(JSON.parse(data.conversation_data));
      setMenuPicks([]);
      setOrderItems([]);
    } catch (e) {
      setAlert({ tone: 'red', text: e?.message || 'Failed to start new chat.' });
    }
  };

  // Auth / restaurant gate
  if (isInitialized && !userId) return <GateCard title={t('chat.gate_signin_title')} sub={t('chat.gate_signin_sub')} cta={t('chat.gate_signin_cta')} onClick={() => navigate('/unified')} />;
  if (isInitialized && !restaurantId) return <GateCard title={t('chat.gate_no_restaurant_title')} sub={t('chat.gate_no_restaurant_sub')} cta={t('chat.gate_no_restaurant_cta')} onClick={() => navigate('/unified/discover')} />;

  return (
    <AppShell dense footer={false}>
      <style>{chatLayoutCss}</style>

      {/* Backdrop (chat uses the brand hero gradient instead of bubble-blue) */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden',
        background: 'radial-gradient(circle at top left, rgba(255,255,255,0.6), transparent 26%), linear-gradient(160deg, #BCE7FF 0%, #5BC2FF 45%, #1E9BFF 100%)',
      }}>
        <div aria-hidden style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.45) 1.5px, transparent 2px)', backgroundSize: '28px 28px', opacity: 0.38, pointerEvents: 'none' }}/>
      </div>

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1280, margin: '0 auto', padding: '16px 16px 24px' }}>

        {/* Restaurant context bar */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.46, ease: EASE }}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
            padding: '10px 14px', borderRadius: 999, marginBottom: 14,
            background: 'rgba(255,255,255,0.96)',
            border: '1px solid rgba(255,255,255,0.96)',
            boxShadow: '0 12px 28px rgba(13,71,161,0.14)',
          }}
        >
          <button
            type="button" onClick={() => navigate(-1)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, background: 'transparent', border: 'none', color: 'var(--cwm-blue-deep)', cursor: 'pointer', fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800 }}
          >
            <BackIcon size={14} /> Back
          </button>
          <div style={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
            <div className="cwm-eyebrow" style={{ color: 'var(--cwm-gray-500)' }}>CHATTING WITH</div>
            <div style={{ fontSize: 15, fontWeight: 900, color: 'var(--cwm-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {restaurantName}
            </div>
            {tableLabel && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 10px', borderRadius: 999,
                background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
                border: '1px solid var(--cwm-blue-light)',
                fontSize: 10.5, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase',
                marginTop: 3,
              }}>
                <span style={{ width: 5, height: 5, borderRadius: 999, background: 'var(--cwm-blue-deep)' }} />
                Table {tableLabel}
              </div>
            )}
          </div>
          <button
            type="button" onClick={newChat}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999, background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)', border: '1.5px solid var(--cwm-blue-light)', cursor: 'pointer', fontFamily: 'var(--cwm-font)', fontSize: 13, fontWeight: 800 }}
          >
            <RefreshIcon size={12} /> {t('chat.reset_chat')}
          </button>
        </motion.div>

        {/* Alert */}
        <AnimatePresence>
          {alert && (
            <motion.div
              key={alert.text}
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              style={{
                padding: '10px 14px', borderRadius: 14, marginBottom: 12,
                background: alert.tone === 'red' ? 'var(--cwm-red-light)' : alert.tone === 'amber' ? 'var(--cwm-amber-light)' : 'var(--cwm-green-light)',
                color: alert.tone === 'red' ? 'var(--cwm-red-text)' : alert.tone === 'amber' ? 'var(--cwm-amber-text)' : 'var(--cwm-green-text)',
                fontSize: 13, fontWeight: 800,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}
              onAnimationComplete={() => setTimeout(() => setAlert(null), 2800)}
            >
              <span>{alert.text}</span>
              <button onClick={() => setAlert(null)} aria-label="dismiss" style={{ background: 'transparent', border: 'none', color: 'inherit', fontWeight: 900, cursor: 'pointer', fontSize: 16 }}>×</button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 2-col layout */}
        <div className="cwm-chat-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 14, alignItems: 'stretch' }}>

          {/* Messages column */}
          <motion.section
            initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.56, delay: 0.06, ease: EASE }}
            className="cwm-sheet cwm-chat-messages"
            style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 520 }}
          >
            {/* Hint */}
            <div style={{ padding: '12px 14px', borderBottom: '1px solid rgba(15,127,232,0.08)' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '6px 12px', borderRadius: 999,
                background: 'var(--cwm-blue-50)', border: '1px solid var(--cwm-blue-light)',
                color: 'var(--cwm-blue-deep)', fontSize: 12, fontWeight: 700,
              }}>
                <SparkleIcon size={12} /> Ask about allergies, swaps, ingredients, or what sounds good
              </div>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              {messages.length === 0 && chatId < 0 && <LoadingMessages />}
              {messages.map((m, i) => <MessageBubble key={i} message={m} index={i} />)}
              <AnimatePresence>
                {loading && <TypingDots />}
              </AnimatePresence>
              <div ref={scrollRef}/>
            </div>

            {/* Suggested picks */}
            {menuPicks.length > 0 && (
              <div style={{ padding: '10px 14px', borderTop: '1px solid rgba(15,127,232,0.08)', background: 'linear-gradient(180deg, #fff, #F7FBFF)' }}>
                <div className="cwm-eyebrow" style={{ marginBottom: 6, color: 'var(--cwm-gray-500)' }}>
                  SUGGESTED PICKS FROM CHAT
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {menuPicks.map((pick, i) => (
                    <motion.button
                      key={pick.id || pick.menu_item_id || i}
                      type="button"
                      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28, delay: i * 0.04 }}
                      whileHover={{ y: -2 }}
                      onClick={() => addToOrder(pick)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '6px 12px', borderRadius: 999,
                        background: '#F8FBFF', border: '1.5px solid var(--cwm-blue-light)',
                        color: 'var(--cwm-blue-deep)',
                        fontFamily: 'var(--cwm-font)', fontSize: 12.5, fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      <PlusIcon size={11} /> {pick.name || pick.title}
                    </motion.button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <form onSubmit={(e) => { e.preventDefault(); send(); }}
                  style={{ padding: '12px 14px', borderTop: '1px solid rgba(15,127,232,0.08)', background: '#fff' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder={t('chat.input_placeholder')}
                    rows={1}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      padding: '12px 16px',
                      borderRadius: 22,
                      border: '1.5px solid var(--cwm-blue-light)',
                      background: '#F7FBFF',
                      color: 'var(--cwm-ink)',
                      fontFamily: 'var(--cwm-font)', fontSize: 14, fontWeight: 500, lineHeight: 1.45,
                      outline: 'none', resize: 'none',
                      maxHeight: 120,
                    }}
                  />
                </div>
                <button
                  type="submit" disabled={!input.trim() || loading}
                  className="cwm-btn"
                  style={{ padding: '12px 16px', opacity: (!input.trim() || loading) ? 0.55 : 1, cursor: (!input.trim() || loading) ? 'not-allowed' : 'pointer' }}
                >
                  <SendIcon size={14} />
                </button>
              </div>
            </form>
          </motion.section>

          {/* Order rail */}
          <motion.aside
            initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.56, delay: 0.14, ease: EASE }}
            className="cwm-chat-rail"
          >
            <OrderRail
              items={orderItems}
              onRemove={removeFromOrder}
              onUpdateQty={updateQty}
              onPlaceOrder={placeOrder}
            />
          </motion.aside>
        </div>
      </div>
    </AppShell>
  );
}

// ---------- sub-components ----------

function MessageBubble({ message, index }) {
  if (!message || message.role === 'system') return null;
  const isUser = message.role === 'user';
  const text = renderableText(message.content);
  return (
    <motion.div
      initial={{ opacity: 0, y: 14, x: isUser ? 18 : -18 }}
      animate={{ opacity: 1, y: 0, x: 0 }}
      transition={{ duration: 0.36, delay: Math.min(index * 0.03, 0.2), ease: EASE }}
      style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}
    >
      <div style={{
        maxWidth: '86%',
        borderRadius: isUser ? '20px 20px 6px 20px' : '20px 20px 20px 6px',
        padding: '12px 14px',
        background: isUser ? 'linear-gradient(180deg, #1E9BFF, #0F7FE8)' : '#fff',
        color: isUser ? '#fff' : 'var(--cwm-ink)',
        boxShadow: isUser ? '0 10px 24px rgba(15,127,232,0.28)' : '0 6px 18px rgba(13,71,161,0.06)',
        border: isUser ? 'none' : '1px solid rgba(15,127,232,0.1)',
        fontSize: 14, lineHeight: 1.55, fontWeight: 500,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>
        {text}
      </div>
    </motion.div>
  );
}

// Safely coerce a chat message payload to a plain string. The backend
// sometimes sends structured content (arrays, nested objects with a .text
// field, etc.) — rendering those directly as React children throws. This
// flattens anything to text without touching unsafe cases.
function renderableText(content) {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content.map((c) => renderableText(c)).filter(Boolean).join('\n');
  }
  if (typeof content === 'object') {
    if (typeof content.text === 'string') return content.text;
    if (typeof content.content === 'string') return content.content;
    try { return JSON.stringify(content); } catch { return '[unrenderable]'; }
  }
  return String(content);
}

function TypingDots() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      style={{ display: 'flex', justifyContent: 'flex-start' }}
    >
      <div style={{
        display: 'inline-flex', gap: 4, padding: '12px 16px',
        borderRadius: '20px 20px 20px 6px',
        background: '#fff', border: '1px solid rgba(15,127,232,0.1)',
        boxShadow: '0 6px 18px rgba(13,71,161,0.06)',
      }}>
        {[0, 1, 2].map((i) => (
          <span key={i} style={{
            width: 7, height: 7, borderRadius: 999,
            background: 'var(--cwm-blue-deep)',
            animation: 'cwmDot 900ms ease-in-out infinite',
            animationDelay: `${i * 150}ms`,
          }}/>
        ))}
      </div>
    </motion.div>
  );
}

function LoadingMessages() {
  return (
    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--cwm-gray-500)', fontSize: 13, fontWeight: 700 }}>
      <div style={{ width: 32, height: 32, margin: '0 auto 10px', border: '3px solid var(--cwm-blue-light)', borderTopColor: 'var(--cwm-blue-deep)', borderRadius: '50%', animation: 'cwmSpin 900ms linear infinite' }}/>
      <style>{`@keyframes cwmSpin { to { transform: rotate(360deg); } }`}</style>
      Starting your chat…
    </div>
  );
}

function OrderRail({ items, onRemove, onUpdateQty, onPlaceOrder }) {
  const subtotal = items.reduce((s, i) => s + (i.price * i.qty), 0);
  const tax = Math.round(subtotal * 0.08);
  const total = subtotal + tax;
  const totalQty = items.reduce((s, i) => s + i.qty, 0);

  return (
    <div className="cwm-sheet" style={{ padding: 18, display: 'flex', flexDirection: 'column', height: '100%', minHeight: 520 }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
        <div>
          <div className="cwm-eyebrow">YOUR PICKS</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.015em' }}>
            {totalQty ? `${totalQty} item${totalQty === 1 ? '' : 's'}` : 'Nothing yet'}
          </div>
        </div>
        <div style={{
          width: 42, height: 42, borderRadius: 14,
          background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
          display: 'grid', placeItems: 'center',
        }}>
          <ReceiptIcon size={20} />
        </div>
      </header>

      <p style={{ margin: '4px 0 14px', fontSize: 13, color: 'var(--cwm-gray-500)', fontWeight: 600 }}>
        {items.length
          ? 'Tap suggested items from the chat to build an order.'
          : 'Ask about dishes, then tap the picks that appear below the chat.'}
      </p>

      <div style={{ flex: 1, overflowY: 'auto', margin: '0 -4px', paddingRight: 2 }}>
        {items.length === 0 ? (
          <div style={{
            padding: '22px 16px', borderRadius: 18,
            background: '#F8FBFF', border: '1.5px dashed rgba(15,127,232,0.25)',
            textAlign: 'center',
          }}>
            <div style={{ color: 'var(--cwm-blue-deep)', marginBottom: 6 }}>
              <SparkleIcon size={22} />
            </div>
            <div style={{ fontSize: 13, color: 'var(--cwm-gray-700)', fontWeight: 600 }}>
              Your saved picks will appear here with quantity controls and totals.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <AnimatePresence initial={false}>
              {items.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 10, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 40, scale: 0.95 }}
                  transition={{ duration: 0.3, delay: index * 0.04 }}
                  style={{
                    padding: '12px 12px',
                    borderRadius: 16,
                    background: '#FCFEFF',
                    border: '1px solid rgba(15,127,232,0.1)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--cwm-ink)', flex: 1, lineHeight: 1.3 }}>
                      {item.name}
                    </div>
                    <button
                      type="button" onClick={() => onRemove(item.id)} aria-label="remove"
                      style={{ background: 'transparent', border: 'none', color: 'var(--cwm-gray-500)', cursor: 'pointer', padding: 2, marginTop: -2 }}
                    >
                      <TrashIcon size={14} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <QtyButton onClick={() => onUpdateQty(item.id, item.qty - 1)} aria-label="decrease">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M5 12h14"/></svg>
                      </QtyButton>
                      <span style={{ minWidth: 22, textAlign: 'center', fontSize: 14, fontWeight: 900, color: 'var(--cwm-ink)' }}>{item.qty}</span>
                      <QtyButton onClick={() => onUpdateQty(item.id, item.qty + 1)} aria-label="increase">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                      </QtyButton>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 900, color: 'var(--cwm-blue-deep)' }}>
                      {item.price > 0 ? `$${((item.price * item.qty) / 100).toFixed(2)}` : 'Ask for price'}
                    </span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {items.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.32 }}
          style={{ marginTop: 14 }}
        >
          <div style={{ height: 1, background: 'var(--cwm-gray-100)', margin: '10px 0' }}/>
          <TotalRow label="Subtotal" value={subtotal} />
          <TotalRow label="Tax (8%)" value={tax} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 900, color: 'var(--cwm-ink)', marginTop: 6 }}>
            <span>Total</span>
            <span>${(total / 100).toFixed(2)}</span>
          </div>
          <button
            className="cwm-btn" type="button" onClick={onPlaceOrder}
            style={{ width: '100%', marginTop: 14, justifyContent: 'center' }}
          >
            Send to kitchen
          </button>
          <p style={{ margin: '10px 2px 0', fontSize: 11, fontWeight: 700, color: 'var(--cwm-gray-500)', textAlign: 'center' }}>
            Prices are estimates pulled from menu data.
          </p>
        </motion.div>
      )}
    </div>
  );
}

function QtyButton({ children, onClick, 'aria-label': aria }) {
  return (
    <button
      type="button" onClick={onClick} aria-label={aria}
      style={{
        width: 26, height: 26, borderRadius: 999,
        display: 'grid', placeItems: 'center',
        background: 'var(--cwm-blue-pale)', color: 'var(--cwm-blue-deep)',
        border: '1px solid var(--cwm-blue-light)', cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function TotalRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, color: 'var(--cwm-gray-700)', marginBottom: 4 }}>
      <span>{label}</span>
      <span>${(value / 100).toFixed(2)}</span>
    </div>
  );
}

function GateCard({ title, sub, cta, onClick }) {
  return (
    <AppShell backdrop>
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '80px 20px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <div className="cwm-eyebrow-badge" style={{ marginBottom: 12 }}>
          <LockIcon size={12} /> CHAT WITH MENU
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 900, color: 'var(--cwm-ink)', letterSpacing: '-0.02em', margin: '0 0 10px' }}>{title}</h1>
        <p style={{ fontSize: 14, color: 'var(--cwm-gray-500)', marginBottom: 22, fontWeight: 500 }}>{sub}</p>
        <button className="cwm-btn" onClick={onClick}>{cta}</button>
      </div>
    </AppShell>
  );
}

// ---------- icons ----------
function BackIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>; }
function RefreshIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4v6h6M23 20v-6h-6M20.49 9A9 9 0 006.49 5L1 10M3.51 15A9 9 0 0017.51 19L23 14"/></svg>; }
function SparkleIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2 6 6 2-6 2-2 6-2-6-6-2 6-2z"/></svg>; }
function PlusIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>; }
function SendIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4z"/></svg>; }
function ReceiptIcon({ size = 20 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2h12v20l-3-2-3 2-3-2-3 2zM9 7h6M9 11h6M9 15h4"/></svg>; }
function TrashIcon({ size = 14 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>; }
function LockIcon({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V8a4 4 0 118 0v3"/></svg>; }

const chatLayoutCss = `
@media (min-width: 1040px) {
  .cwm-chat-grid {
    grid-template-columns: minmax(0, 1.45fr) minmax(320px, 0.85fr) !important;
  }
  .cwm-chat-messages {
    min-height: calc(100vh - 180px) !important;
  }
  .cwm-chat-rail {
    min-height: calc(100vh - 180px);
  }
}
@keyframes cwmDot { 0%, 60%, 100% { transform: translateY(0); opacity: 0.45; } 30% { transform: translateY(-4px); opacity: 1; } }
`;
